import { FC, useRef } from "react";
import { css } from "@emotion/react";
import f from "@/module/Utils/emotion";
import v from "@/module/Utils/variable";
import Util from "@/module/Utils/Util";
import { motion } from "framer-motion";
import { buttonAnimation } from "@/module/animation/animationParams";

// Components
import Picture from "@/components/Atoms/Picture";
import DefaultButton from "@/components/Molecules/DefaultButton";
import Link from "@/components/Atoms/Link";

// Images
import TextSeeUbb from "@/images/opening/text_see_ubb";
import Ja from "@/images/common/text_jp.svg";
import En from "@/images/common/text_en.svg";
import Gradation_PC from "@/images/opening/float_gradation_pc.png";
import Gradation_SP from "@/images/opening/float_gradation_sp.png";
import TextNext from "@/images/common/text_next";

interface NavProps {
  isShow: boolean;
  link: string;
  buttonType: "next" | "seeubb";
  onClickButton: () => void;
  gaPage: string;
}

const DescriptionFloatNav: FC<NavProps> = (props) => {
  //Initialize
  const nextButtonRef = useRef<HTMLDivElement>();
  // Styles
  const floatStyle = css({
    position: "sticky",
    bottom: 0,
    width: "100%",
    pointerEvents: "none",
    touchAction: "none",
    backgroundSize: "cover",
    backgroundPosition: "top left",
    backgroundRepeat: "no-repeat",
    [f.pc()]: {
      height: f.vw(400),
      backgroundImage: `url(${Gradation_PC})`,
      paddingTop: f.vw(100),
      marginTop: f.vw(-100),
    },
    [f.sp()]: {
      height: f.vw(392, v.spWidth),
      backgroundImage: `url(${Gradation_SP})`,
    },
  });

  const nextStyle = css({
    margin: `0 auto`,
    visibility: "hidden",
    opacity: 0,
    transition: "opacity 0.5s ease-out",
    [f.sp()]: {
      width: f.vw(428, v.spWidth),
      height: f.vw(100 + 162, v.spWidth),
      paddingTop: f.vw(162, v.spWidth),
    },
    [f.pc()]: {
      width: f.vw(220),
      height: f.vw(62 + 150, v.pcWidth),
      paddingTop: f.vw(150),
    },
    "&.is-show": {
      opacity: 1,
      visibility: "visible",
    },
  });

  const topNextImageStyle = css({
    "&.is-seeubb": {
      [f.sp()]: {
        width: f.vw(161.28, v.spWidth),
      },
      [f.pc()]: {
        width: f.vw(69.74, v.pcWidth),
      },
    },
    "&.is-next": {
      [f.sp()]: {
        width: f.vw(85.93, v.spWidth),
      },
      [f.pc()]: {
        width: f.vw(42.97, v.pcWidth),
      },
    },
    img: {
      verticalAlign: "baseline",
    },
  });

  const topFooterLanguageStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [f.sp()]: {
      marginTop: f.vw(56, v.spWidth),
      marginBottom: f.vw(87.6, v.spWidth),
    },
    [f.pc()]: {
      marginTop: f.vw(25, v.pcWidth),
      marginBottom: f.vw(50),
    },
  });

  const topFooterLineStyle = css({
    width: "1px",
    backgroundColor: "#fff",
    [f.sp()]: {
      height: f.vw(17.23, v.spWidth),
      margin: `0 ${f.vw(14, v.spWidth)}`,
    },
    [f.pc()]: {
      height: f.vw(10),
      margin: `0 ${f.vw(14)}`,
    },
  });

  const topFooterLangStyle = css({
    position: "relative",
    [f.sp()]: {
      height: f.vw(22.01, v.spWidth),
      paddingBottom: f.vw(5, v.spWidth),
    },
    [f.pc()]: {
      height: f.vw(12.95, v.pcWidth),
      paddingBottom: f.vw(5),
    },
    img: {
      width: "auto",
      height: "100%",
    },
  });

  const activeLanguage = css({
    pointerEvents: "none",
    userSelect: "none",
    "&:after": {
      content: "''",
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: "1px",
      backgroundColor: "#fff",
    },
  });
  const pushPageNext = (resolve) => {
    props.onClickButton();
    Util.sleep(v.pageTransition).then(() => {
      resolve();
    });
  };
  return (
    <div css={floatStyle}>
      <div
        css={nextStyle}
        className={props.isShow ? "is-show" : ""}
        ref={nextButtonRef}
      >
        <DefaultButton icon={"arrow"} iconColor={"red"}>
          <Link
            to={props.link}
            onBeforeLeave={pushPageNext}
            gaClass={`u-ga-seeubb-${props.gaPage}_btn-next`}
          >
            <div css={topNextImageStyle} className={`is-${props.buttonType}`}>
              {props.buttonType === "seeubb" && <TextSeeUbb />}
              {props.buttonType === "next" && <TextNext />}
            </div>
          </Link>
        </DefaultButton>
      </div>
      <motion.div
        initial={buttonAnimation.initial}
        animate={buttonAnimation.animate}
        transition={buttonAnimation.transition}
        css={topFooterLanguageStyle}
      >
        <a
          href={"/seesaydo/ja/seeubb/"}
          css={css(
            process.env.GATSBY_LANG === "ja" ? activeLanguage : null,
            topFooterLangStyle
          )}
          className={`u-ga-seeubb-${props.gaPage}_lang-ja`}
        >
          <Picture path={Ja} alt={"JA"} />
        </a>
        <span css={topFooterLineStyle} />
        <a
          href={"/seesaydo/en/seeubb/"}
          css={css(
            process.env.GATSBY_LANG === "en" ? activeLanguage : null,
            topFooterLangStyle
          )}
          className={`u-ga-seeubb-${props.gaPage}_lang-en`}
        >
          <Picture path={En} alt={"EN"} />
        </a>
      </motion.div>
    </div>
  );
};

export default DescriptionFloatNav;
